import React, { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { AcceptIcon, CancelIcon, ErrorIcon, InfoIcon, WarningIcon } from "tombac-icons";
import { tombac } from "../../shared";
import { Button } from "../Button";
import { Box } from "../Styling/Box";
import { Label, Text } from "../Typography";
const ToastButton = styled(Button).attrs(() => ({
  children: /*#__PURE__*/React.createElement(CancelIcon, null),
  shape: "circle",
  variant: "flat"
})).withConfig({
  displayName: "Toast__ToastButton"
})(["align-self:stretch;border-left:", " solid ", ";border-radius:0;color:", ";font-size:inherit;height:auto;padding:0;width:", ";"], tombac.unit(1), tombac.color("neutral", 400), tombac.color("neutral", 600), tombac.space(6));
const ToastContainer = styled.div.withConfig({
  displayName: "Toast__ToastContainer"
})(["all:initial;box-sizing:border-box;opacity:0;width:", ";transform:translateX( ", " );", ""], _ref => {
  let {
    $width
  } = _ref;
  return $width;
}, _ref2 => {
  let {
    animEdge
  } = _ref2;
  return `${animEdge === "left" ? "-" : ""}100%`;
}, _ref3 => {
  let {
    $height,
    animEdge,
    state
  } = _ref3;
  return $height && css(["height:", ";opacity:", ";transform:translateX( ", " );transition:transform 0.2s ", ",opacity 0.2s ", ",height 0.2s ", ";transition-timing-function:ease-out;"], () => state === "added" || state === "exiting" ? 0 : $height + "px", () => state === "added" || state === "exiting" ? 0 : 1, () => state === "added" || state === "exiting" ? `${animEdge === "left" ? "-" : ""}100%` : 0, () => state === "entering" && "0.2s", () => state === "entering" && "0.2s", () => state === "exiting" && "0.2s");
});
const ToastLayout = styled.div.withConfig({
  displayName: "Toast__ToastLayout"
})(["all:initial;align-items:center;background-color:", ";border:", " solid ", ";border-radius:", ";box-shadow:", " ", " ", ";display:flex;margin-top:", ";overflow:hidden;position:relative;", " ", ""], tombac.color.white, tombac.unit(1), tombac.color("neutral", 400), tombac.unit(5), tombac.unit(0, 1, 14, 0), " ", tombac.alpha("neutral", 8), tombac.space(1), _ref4 => {
  let {
    toastType,
    dismissable
  } = _ref4;
  return dismissable ? css(["padding-left:", ";"], toastType !== "loading" ? "0" : tombac.space(4)) : css(["padding-left:", ";padding-right:", ";"], toastType !== "loading" ? "0" : tombac.space(4), toastType !== "loading" ? "0" : css(["calc(", " + ", ")"], tombac.space(5), tombac.unit(5)));
}, _ref5 => {
  let {
    barColor
  } = _ref5;
  return barColor && css(["&::before{background-color:", ";content:\"\";display:block;height:100%;position:absolute;width:", ";}"], tombac.color(barColor), tombac.unit(5));
});
const ToastIcon = styled.div.withConfig({
  displayName: "Toast__ToastIcon"
})(["all:initial;color:", ";display:block;font-size:", ";padding:", " calc(", " + ", ");", ""], _ref6 => {
  let {
    $color
  } = _ref6;
  return $color && tombac.color($color);
}, tombac.unit(19), tombac.space(1, 1, 1), tombac.space(1), tombac.unit(5), _ref7 => {
  let {
    withContent
  } = _ref7;
  return withContent && css(["align-self:flex-start;margin-top:", ";"], tombac.space(1));
});
const toastTypeIconMap = {
  alert: WarningIcon,
  danger: ErrorIcon,
  info: InfoIcon,
  success: AcceptIcon
};
const toastTypeColorMap = {
  alert: "alert",
  danger: "danger",
  default: undefined,
  info: "accent",
  success: "success",
  loading: "primary"
};
export const Toast = _ref8 => {
  let {
    dismissRequested = false,
    message,
    options: {
      autoDismiss: baseAutoDismiss,
      autoDismissDelay = 5000,
      content,
      dismissable: baseDismissable,
      icon,
      onDismiss,
      placement
    },
    onDissmised,
    type
  } = _ref8;
  const [state, setState] = useState("added");
  const [paused, setPaused] = useState(false);
  const isLoadingType = type === "loading";
  const autoDismiss = baseAutoDismiss === undefined && !isLoadingType ? true : baseAutoDismiss;
  const dismissable = baseDismissable === undefined && !isLoadingType ? true : baseDismissable;
  const [containerRef, setContainerRef] = useState(null);
  const height = useMemo(() => containerRef === null || containerRef === void 0 ? void 0 : containerRef.getBoundingClientRect().height, [containerRef]);
  useEffect(() => {
    if (height !== undefined) {
      requestAnimationFrame(() => setState("entering"));
    }
  }, [height]);
  useEffect(() => {
    let timeout;
    if (autoDismiss && !paused) {
      timeout = window.setTimeout(dismiss, autoDismissDelay);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [autoDismiss, autoDismissDelay, paused]);
  useEffect(() => {
    if (dismissRequested === true && state === "entered") {
      dismiss();
    }
  }, [dismissRequested, state]);
  useEffect(() => {
    if (state === "exited") {
      onDissmised();
    }
  }, [state]);
  const dismiss = () => {
    setState("exiting");
    if (onDismiss) {
      onDismiss();
    }
  };
  const handleTransitionEnd = event => {
    const {
      propertyName
    } = event;
    setState(state => {
      if (state === "entering" && propertyName === "transform") {
        return "entered";
      } else if (state === "exiting" && propertyName === "height") {
        return "exited";
      }
      return state;
    });
  };
  const Icon = toastTypeIconMap === null || toastTypeIconMap === void 0 ? void 0 : toastTypeIconMap[type];
  const color = toastTypeColorMap[type];
  return /*#__PURE__*/React.createElement(ToastContainer, {
    $height: height,
    animEdge: placement.includes("left") ? "left" : "right",
    onTransitionEnd: handleTransitionEnd,
    ref: setContainerRef,
    state: state,
    $width: isLoadingType ? "auto" : "100%"
  }, /*#__PURE__*/React.createElement(ToastLayout, {
    barColor: isLoadingType ? undefined : color,
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    dismissable: dismissable,
    toastType: type
  }, /*#__PURE__*/React.createElement(ToastIcon, {
    $color: color,
    withContent: !!content
  }, icon !== null && icon !== void 0 ? icon : Icon && /*#__PURE__*/React.createElement(Icon, null)), /*#__PURE__*/React.createElement(Box, {
    $display: "flex",
    $flexDirection: "column",
    $flex: "1",
    $marginBottom: "1.9sp",
    $marginTop: "1.9sp",
    $minWidth: "0",
    $pr: isLoadingType && dismissable ? "calc( 5sp + 5u )" : "0"
  }, /*#__PURE__*/React.createElement(Label, {
    $overflowWrap: "break-word",
    as: "div"
  }, message), content && /*#__PURE__*/React.createElement(Text, {
    $mt: "0.5sp",
    as: "div",
    size: "s"
  }, content)), " ", dismissable && /*#__PURE__*/React.createElement(ToastButton, {
    onClick: dismiss
  })));
};
//# sourceMappingURL=Toast.js.map